import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Badge, Button, Card, ErrorNote, Field, Input, Spinner, Stat } from "../components/ui";
import { relative } from "../lib/format";

const ROLES = ["viewer", "member", "admin", "owner"];

export default function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["tenant"], queryFn: api.tenant.get });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.auth.me });
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: api.tenant.members });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");

  const rename = useMutation({
    mutationFn: () => api.tenant.update(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const invite = useMutation({
    mutationFn: () => api.tenant.invite(email, role),
    onSuccess: () => {
      setEmail("");
      void qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.tenant.removeMember(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members"] }),
  });

  if (isLoading || !data) return <Spinner />;

  const canAdmin = me ? ["admin", "owner"].includes(me.user.role) || me.user.platform_admin : false;

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-0.5 text-sm text-muted">{data.tenant.name}</p>
      </header>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Stat label="Projects" value={`${data.projects} / ${data.tenant.max_projects}`} />
        <Stat label="Deployed apps" value={`${data.deployed_apps} / ${data.tenant.max_deployed_apps}`} />
        <Stat label="Members" value={members?.total ?? 0} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-3 text-sm font-medium">Organisation</h2>
          {rename.error && (
            <div className="mb-3">
              <ErrorNote error={rename.error} />
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Field label="Name">
              <Input value={name || data.tenant.name} onChange={setName} />
            </Field>
            <div>
              <Button onClick={() => rename.mutate()} disabled={!canAdmin || rename.isPending}>
                {rename.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
            <p className="text-2xs leading-relaxed text-muted">
              Quotas and plan are set by Atelier, not here — an organisation that could raise its own limits
              would not have limits.
            </p>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-3 text-sm font-medium">Members</h2>
          {invite.error && (
            <div className="mb-3">
              <ErrorNote error={invite.error} />
            </div>
          )}

          <ul className="mb-4 flex flex-col gap-2">
            {members?.items.map((u) => (
              <li key={u.id} className="flex items-center justify-between gap-3 border-b border-hairline/60 pb-2 last:border-0">
                <div className="min-w-0">
                  <p className="truncate text-sm">{u.name}</p>
                  <p className="truncate text-2xs text-muted">
                    {u.email} · last seen {relative(u.last_active_at)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={u.role === "owner" ? "clay" : "muted"}>{u.role}</Badge>
                  {canAdmin && u.id !== me?.user.id && (
                    <Button size="sm" variant="quiet" onClick={() => remove.mutate(u.id)}>
                      Remove
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {canAdmin && (
            <div className="flex flex-col gap-3 border-t border-hairline pt-4">
              <Field label="Invite" hint="They sign in with a code sent to this address — there is no invite link.">
                <Input value={email} onChange={setEmail} type="email" placeholder="teammate@example.com" />
              </Field>
              <div className="flex items-center gap-2">
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="rounded-card border border-hairline bg-paper px-3 py-2 text-sm capitalize outline-none focus:border-clay"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <Button onClick={() => invite.mutate()} disabled={!email.includes("@") || invite.isPending}>
                  {invite.isPending ? "Adding…" : "Add member"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
